import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowData,
  type MRT_SortingState,
  type MRT_PaginationState,
  type MRT_ExpandedState,
  type MRT_Row,
  type MRT_TableOptions,
  type MRT_ColumnSizingState,
  type MRT_VisibilityState,
} from 'material-react-table';
import { Box, CircularProgress, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { FullscreenExitIcon, MoreVertIcon as MoreVert } from "../../../../icons";
import { observer } from 'mobx-react-lite';
import {
  CLEAR_FILTER_LABEL,
  ColumnType,
  FilterOperator,
  defaultOperatorByType,
  isOperatorAvailable,
  MRTFilter,
  operatorNeedsValue,
} from '../../../services/mrtFilterService';
import { FilterCell } from './FilterCell';

// ============================================================================
// Types
// ============================================================================

export interface DataGridColumn<T extends MRT_RowData> extends Omit<MRT_ColumnDef<T>, 'filterFn'> {
  columnType?: ColumnType;
  /**
   * La columna admite null (una fecha opcional). Solo así su filtro ofrece
   * "Vacío" y "No vacío": en una fecha obligatoria el server rechaza la
   * pregunta. Sin el indicador no se ofrecen.
   */
  nullable?: boolean;
  filterEnabled?: boolean;
  sortingEnabled?: boolean;
}

export interface DataGridRowAction<T> {
  label: string;
  icon?: React.ReactNode;
  onClick: (row: T, event?: React.MouseEvent) => void;
  isEnabled?: (row: T) => boolean;
  isVisible?: (row: T) => boolean;
}

export interface DataGridProps<T extends MRT_RowData> {
  // Datos
  columns: DataGridColumn<T>[];
  data: T[];
  isLoading?: boolean;
  getRowId?: (row: T) => string;

  // === Estados Controlados (Store → DataGrid → Store) ===

  // Ordenamiento
  sorting?: MRT_SortingState;
  onSortingChange?: (sorting: MRT_SortingState) => void;

  // Paginación
  pagination?: MRT_PaginationState;
  onPaginationChange?: (pagination: MRT_PaginationState) => void;
  rowCount?: number;
  manualPagination?: boolean;
  enablePagination?: boolean;

  // Visibilidad de columnas
  columnVisibility?: MRT_VisibilityState;
  onColumnVisibilityChange?: (visibility: MRT_VisibilityState) => void;

  // Orden de columnas
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;

  // Tamaño de columnas
  columnSizing?: MRT_ColumnSizingState;
  onColumnSizingChange?: (sizing: MRT_ColumnSizingState) => void;

  // Filtros (semi-controlado: UI interna, output al store)
  onFiltersChange?: (filters: MRTFilter[]) => void;
  enableFiltering?: boolean;
  initialFilters?: { columnName: string; operation?: string; value?: any }[];

  // Estados de UI controlados (sincronizados con ListToolbox via store)
  showColumnFilters?: boolean;
  onShowColumnFiltersChange?: (value: boolean) => void;
  isFullScreen?: boolean;
  onIsFullScreenChange?: (value: boolean) => void;

  // === Estados Internos (UI transitorio) ===

  // Expansión de filas
  enableExpanding?: boolean;
  getSubRows?: (row: T) => T[] | undefined;
  onExpandRow?: (row: T) => Promise<void>;
  isLoadingSubRows?: (rowId: string) => boolean;
  renderDetailPanel?: (props: { row: MRT_Row<T> }) => React.ReactNode;

  // Selección de filas (single)
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: T[]) => void;

  // Multi-selección controlada
  multiSelectMode?: boolean;
  selectedRowIds?: Set<string>;
  onSelectedRowIdsChange?: (ids: Set<string>) => void;

  // Eventos de fila
  onRowClick?: (row: T, event?: React.MouseEvent) => void;
  selectedRowId?: string | null;

  // Acciones de fila
  rowActions?: DataGridRowAction<T>[];

  // === Configuración Visual ===
  enableColumnResizing?: boolean;
  enableColumnOrdering?: boolean;
  enableFullScreenToggle?: boolean;
  enableColumnFilters?: boolean;
  enableShowHideFilters?: boolean;
  initialShowFilters?: boolean;
  enableGlobalFilter?: boolean;
  enableHiding?: boolean;
  language?: 'es' | 'en' | 'pt';

  // Props adicionales para MRT
  muiTableProps?: MRT_TableOptions<T>['muiTableProps'];
  muiTableBodyRowProps?: MRT_TableOptions<T>['muiTableBodyRowProps'];
}

// ============================================================================
// Columnas especiales de MRT que debemos manejar en el orden
// ============================================================================
const SPECIAL_COLUMNS = ['mrt-row-expand', 'mrt-row-select', 'row-actions'];

function addSpecialColumnsToOrder(order: string[], hasExpanding: boolean, hasActions: boolean, hasMultiSelect: boolean): string[] {
  const withoutSpecial = order.filter(c => !SPECIAL_COLUMNS.includes(c));
  const prefix: string[] = [];
  if (hasMultiSelect) prefix.push('mrt-row-select');
  if (hasExpanding) prefix.push('mrt-row-expand');
  if (hasActions) prefix.push('row-actions');
  return [...prefix, ...withoutSpecial];
}

function removeSpecialColumnsFromOrder(order: string[]): string[] {
  return order.filter(c => !SPECIAL_COLUMNS.includes(c));
}

// ============================================================================
// DataGrid Component
// ============================================================================

function DataGridInner<T extends MRT_RowData>({
  // Datos
  columns,
  data,
  isLoading = false,
  getRowId,

  // Estados controlados
  sorting: externalSorting,
  onSortingChange,
  pagination: externalPagination,
  onPaginationChange,
  rowCount,
  manualPagination = true,
  enablePagination = true,
  columnVisibility: externalColumnVisibility,
  onColumnVisibilityChange,
  columnOrder: externalColumnOrder,
  onColumnOrderChange,
  columnSizing: externalColumnSizing,
  onColumnSizingChange,
  onFiltersChange,
  enableFiltering = true,
  initialFilters,

  // Estados de UI controlados
  showColumnFilters: externalShowColumnFilters,
  onShowColumnFiltersChange,
  isFullScreen: externalIsFullScreen,
  onIsFullScreenChange,

  // Estados internos
  enableExpanding = false,
  getSubRows,
  onExpandRow,
  isLoadingSubRows,
  renderDetailPanel,
  enableRowSelection = false,
  multiSelectMode = false,
  selectedRowIds,
  onSelectedRowIdsChange,
  onRowClick,
  selectedRowId,
  rowActions,

  // Configuración visual
  enableColumnResizing = true,
  enableColumnOrdering = true,
  enableFullScreenToggle = false,
  enableColumnFilters = true,
  enableShowHideFilters = true,
  initialShowFilters = false,
  enableGlobalFilter = false,
  enableHiding = true,
  language = 'es',
  muiTableProps,
  muiTableBodyRowProps,
}: DataGridProps<T>) {

  // ==========================================================================
  // Refs para valores actuales (necesarios para updaters de MRT)
  // ==========================================================================
  const sortingRef = useRef(externalSorting);
  const paginationRef = useRef(externalPagination);
  const columnVisibilityRef = useRef(externalColumnVisibility);
  const columnOrderRef = useRef(externalColumnOrder);
  const columnSizingRef = useRef(externalColumnSizing);

  // Sincronizar refs con props
  useEffect(() => { sortingRef.current = externalSorting; }, [externalSorting]);
  useEffect(() => { paginationRef.current = externalPagination; }, [externalPagination]);
  useEffect(() => { columnVisibilityRef.current = externalColumnVisibility; }, [externalColumnVisibility]);
  useEffect(() => { columnOrderRef.current = externalColumnOrder; }, [externalColumnOrder]);
  useEffect(() => { columnSizingRef.current = externalColumnSizing; }, [externalColumnSizing]);

  // ==========================================================================
  // Estados Internos (UI transitorio, no necesitan persistencia)
  // ==========================================================================

  // Filtros: estado UI interno, convertido a MRTFilter[] para el store
  const [filterState, setFilterState] = useState<Record<string, { value: string; operator: FilterOperator }>>({});

  // Sincronizar filtros iniciales del profile (llegan asíncronamente desde api.me)
  // Solo aplicar filtros de columnas que existen actualmente (el cliente manda)
  const initialFiltersApplied = useRef(false);
  const columnsById = useMemo(
    () => new Map(columns.map(c => [(c.id || c.accessorKey) as string, c])),
    [columns]
  );
  useEffect(() => {
    try {
      if (initialFilters && initialFilters.length > 0 && !initialFiltersApplied.current) {
        initialFiltersApplied.current = true;
        const newFilterState: Record<string, { value: string; operator: FilterOperator }> = {};
        for (const filter of initialFilters) {
          // Solo aplicar si la columna existe en el cliente actual, y si el
          // filtro tiene valor o su operador no lo lleva (vacío / no vacío).
          // Un perfil viejo, o una columna que cambió de tipo, no puede dejar
          // un "vacío" donde la columna no lo admite: ni se ofrece ni lo acepta
          // el server.
          const column = columnsById.get(filter.columnName);
          const operator = (filter.operation as FilterOperator) || 'contains';
          const hasValue = filter.value != null && filter.value !== '';
          if (
            column &&
            (hasValue || !operatorNeedsValue(operator)) &&
            isOperatorAvailable(column.columnType || 'string', operator, column.nullable)
          ) {
            newFilterState[filter.columnName] = {
              value: hasValue ? String(filter.value) : '',
              operator,
            };
          }
        }
        // Siempre, aunque no haya quedado ninguno: si se descartó algo, el estado
        // nuevo hace que se emita otra vez (onFiltersChange) y el store, que pudo
        // haber recibido el perfil antes, se alinee con lo que la pantalla muestra.
        setFilterState(newFilterState);
      }
    } catch (error) {
      // Nunca fallar por filtros inválidos del profile
      console.warn('[DataGrid] Error applying initial filters:', error);
    }
  }, [initialFilters, columnsById]);

  // Expansión de filas
  const [expanded, setExpanded] = useState<MRT_ExpandedState>({});

  // Estados de UI (controlados o internos)
  // showColumnFilters - usa externo si disponible, sino interno
  const [internalShowColumnFilters, setInternalShowColumnFilters] = useState(initialShowFilters);
  const showColumnFilters = externalShowColumnFilters ?? internalShowColumnFilters;
  const handleShowColumnFiltersChange = useCallback((value: boolean) => {
    if (onShowColumnFiltersChange) {
      onShowColumnFiltersChange(value);
    } else {
      setInternalShowColumnFilters(value);
    }
  }, [onShowColumnFiltersChange]);

  // density - siempre 'compact' para máxima densidad de información
  const density = 'compact' as const;

  // isFullScreen - usa externo si disponible, sino interno
  const [internalIsFullScreen, setInternalIsFullScreen] = useState(false);
  const isFullScreen = externalIsFullScreen ?? internalIsFullScreen;
  const handleIsFullScreenChange = useCallback((value: boolean) => {
    if (onIsFullScreenChange) {
      onIsFullScreenChange(value);
    } else {
      setInternalIsFullScreen(value);
    }
  }, [onIsFullScreenChange]);

  // Menú de acciones
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuRow, setActionMenuRow] = useState<T | null>(null);

  // ==========================================================================
  // Mapa de tipos de columna para filtros
  // ==========================================================================
  const columnTypeMap = useMemo(() => {
    const map: Record<string, ColumnType> = {};
    columns.forEach(col => {
      const colId = (col.id || col.accessorKey) as string;
      if (colId && col.columnType) {
        map[colId] = col.columnType;
      }
    });
    return map;
  }, [columns]);

  // ==========================================================================
  // Handlers para Estados Controlados
  // ==========================================================================

  const handleSortingChange = useCallback((updater: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState)) => {
    if (!onSortingChange) return;
    const newValue = typeof updater === 'function'
      ? updater(sortingRef.current || [])
      : updater;
    onSortingChange(newValue);
  }, [onSortingChange]);

  const handlePaginationChange = useCallback((updater: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => {
    if (!onPaginationChange) return;
    const newValue = typeof updater === 'function'
      ? updater(paginationRef.current || { pageIndex: 0, pageSize: 10 })
      : updater;
    onPaginationChange(newValue);
  }, [onPaginationChange]);

  const handleColumnVisibilityChange = useCallback((updater: MRT_VisibilityState | ((old: MRT_VisibilityState) => MRT_VisibilityState)) => {
    if (!onColumnVisibilityChange) return;
    const newValue = typeof updater === 'function'
      ? updater(columnVisibilityRef.current || {})
      : updater;
    onColumnVisibilityChange(newValue);
  }, [onColumnVisibilityChange]);

  const handleColumnOrderChange = useCallback((updater: string[] | ((old: string[]) => string[])) => {
    if (!onColumnOrderChange) return;
    const currentWithSpecial = addSpecialColumnsToOrder(
      columnOrderRef.current || [],
      enableExpanding,
      !!(rowActions && rowActions.length > 0),
      multiSelectMode
    );
    const newValue = typeof updater === 'function' ? updater(currentWithSpecial) : updater;
    // Enviar al store sin columnas especiales
    onColumnOrderChange(removeSpecialColumnsFromOrder(newValue));
  }, [onColumnOrderChange, enableExpanding, rowActions, multiSelectMode]);

  const handleColumnSizingChange = useCallback((updater: MRT_ColumnSizingState | ((old: MRT_ColumnSizingState) => MRT_ColumnSizingState)) => {
    if (!onColumnSizingChange) return;
    const newValue = typeof updater === 'function'
      ? updater(columnSizingRef.current || {})
      : updater;
    onColumnSizingChange(newValue);
  }, [onColumnSizingChange]);

  // ==========================================================================
  // Handlers para Filtros (semi-controlado)
  // ==========================================================================

  const handleFilterChange = useCallback((
    columnId: string,
    value: string,
    operator: FilterOperator
  ) => {
    setFilterState(prev => {
      const newState = { ...prev };
      if (value === '' && operator === defaultOperatorByType[columnTypeMap[columnId] || 'string']) {
        delete newState[columnId];
      } else {
        newState[columnId] = { value, operator };
      }
      return newState;
    });
  }, [columnTypeMap]);

  // Sincronizar filterState → onFiltersChange (evita stale closure)
  useEffect(() => {
    if (!onFiltersChange) return;

    const filters: MRTFilter[] = Object.entries(filterState)
      .filter(([, state]) => state.value !== '' || !operatorNeedsValue(state.operator))
      .map(([columnId, state]) => ({
        columnName: columnId,
        columnType: columnTypeMap[columnId] || 'string',
        operator: state.operator,
        value: state.value,
      }));

    onFiltersChange(filters);
  }, [filterState, columnTypeMap, onFiltersChange]);

  // ==========================================================================
  // Handlers para Expansión
  // ==========================================================================

  const handleExpandedChange = useCallback((updater: MRT_ExpandedState | ((old: MRT_ExpandedState) => MRT_ExpandedState)) => {
    const newExpanded = typeof updater === 'function' ? updater(expanded) : updater;

    // Detectar filas recién expandidas para cargar datos
    if (onExpandRow) {
      const prevKeys = Object.keys(expanded as Record<string, boolean>).filter(k => (expanded as Record<string, boolean>)[k]);
      const newKeys = Object.keys(newExpanded as Record<string, boolean>).filter(k => (newExpanded as Record<string, boolean>)[k]);
      const newlyExpanded = newKeys.filter(k => !prevKeys.includes(k));

      for (const rowId of newlyExpanded) {
        const row = data.find(r => (getRowId ? getRowId(r) : (r as any).id) === rowId);
        if (row) onExpandRow(row);
      }
    }

    setExpanded(newExpanded);
  }, [expanded, data, getRowId, onExpandRow]);

  // ==========================================================================
  // Multi-selección controlada
  // ==========================================================================

  // Convertir Set<string> a Record<string, boolean> para MRT
  const rowSelection = useMemo(() => {
    if (!multiSelectMode || !selectedRowIds) return {};
    const selection: Record<string, boolean> = {};
    selectedRowIds.forEach(id => { selection[id] = true; });
    return selection;
  }, [multiSelectMode, selectedRowIds]);

  // Handler para cambios en la selección de MRT
  const handleRowSelectionChange = useCallback((
    updater: Record<string, boolean> | ((old: Record<string, boolean>) => Record<string, boolean>)
  ) => {
    if (!onSelectedRowIdsChange) return;
    const newValue = typeof updater === 'function' ? updater(rowSelection) : updater;
    const newSet = new Set(Object.keys(newValue).filter(k => newValue[k]));
    onSelectedRowIdsChange(newSet);
  }, [onSelectedRowIdsChange, rowSelection]);

  // ==========================================================================
  // Handlers para Menú de Acciones
  // ==========================================================================

  const handleActionMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, row: T) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRow(row);
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  }, []);

  const handleActionClick = useCallback((action: DataGridRowAction<T>, event: React.MouseEvent) => {
    if (actionMenuRow) action.onClick(actionMenuRow, event);
    handleActionMenuClose();
  }, [actionMenuRow, handleActionMenuClose]);

  // ==========================================================================
  // Columnas MRT
  // ==========================================================================

  const mrtColumns = useMemo<MRT_ColumnDef<T>[]>(() => {
    const baseColumns = columns.map(col => {
      const columnId = (col.id || col.accessorKey) as string;
      const colType = col.columnType || 'string';
      const filterEnabled = col.filterEnabled !== false && enableFiltering && enableColumnFilters;

      return {
        ...col,
        enableColumnFilter: filterEnabled,
        enableSorting: col.sortingEnabled !== false,
        filterVariant: 'text',
        Filter: filterEnabled
          ? () => {
              const currentState = filterState[columnId] || {
                value: '',
                operator: defaultOperatorByType[colType],
              };
              return (
                <FilterCell
                  columnId={columnId}
                  columnType={colType}
                  value={currentState.value}
                  operator={currentState.operator}
                  onChange={handleFilterChange}
                  language={language}
                  nullable={col.nullable}
                />
              );
            }
          : undefined,
        filterFn: () => true, // Filtrado manual en servidor
      } as MRT_ColumnDef<T>;
    });

    // Agregar columna de acciones al inicio
    if (rowActions && rowActions.length > 0) {
      const actionsColumn: MRT_ColumnDef<T> = {
        id: 'row-actions',
        header: '',
        size: 50,
        enableColumnFilter: false,
        enableSorting: false,
        enableResizing: false,
        enableColumnOrdering: false,
        Cell: ({ row }) => (
          <IconButton size="small" onClick={(e) => handleActionMenuOpen(e, row.original)}>
            <MoreVert fontSize="small" />
          </IconButton>
        ),
      };
      return [actionsColumn, ...baseColumns];
    }

    return baseColumns;
  }, [columns, filterState, handleFilterChange, language, enableFiltering, enableColumnFilters, rowActions, handleActionMenuOpen]);

  // ==========================================================================
  // Orden de columnas con columnas especiales
  // ==========================================================================

  const effectiveColumnOrder = useMemo(() => {
    if (!externalColumnOrder || externalColumnOrder.length === 0) return undefined;
    return addSpecialColumnsToOrder(
      externalColumnOrder,
      enableExpanding,
      !!(rowActions && rowActions.length > 0),
      multiSelectMode
    );
  }, [externalColumnOrder, enableExpanding, rowActions, multiSelectMode]);

  // ==========================================================================
  // Configuración de MRT
  // ==========================================================================

  const table = useMaterialReactTable<T>({
    columns: mrtColumns,
    data,

    // Estado controlado - viene directamente de props (store)
    state: {
      isLoading,
      sorting: externalSorting,
      pagination: externalPagination,
      columnVisibility: externalColumnVisibility ?? {},
      columnOrder: effectiveColumnOrder,
      columnSizing: externalColumnSizing ?? {},
      expanded,
      showColumnFilters,
      density,
      isFullScreen,
      rowSelection: multiSelectMode ? rowSelection : {},
    },

    // Callbacks - actualizan el store
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnSizingChange: handleColumnSizingChange,
    onExpandedChange: handleExpandedChange,
    onShowColumnFiltersChange: enableShowHideFilters
      ? (updater) => {
          const newValue = typeof updater === 'function' ? updater(showColumnFilters) : updater;
          handleShowColumnFiltersChange(newValue);
        }
      : undefined,
    // density es fijo en 'compact', no necesita callback
    onIsFullScreenChange: enableFullScreenToggle
      ? (updater) => {
          const newValue = typeof updater === 'function' ? updater(isFullScreen) : updater;
          handleIsFullScreenChange(newValue);
        }
      : undefined,

    // Configuración de ordenamiento
    enableSorting: true,
    enableSortingRemoval: false, // Solo asc/desc, sin clear
    manualSorting: !!onSortingChange,

    // Configuración de filtrado
    enableColumnFilters,
    enableGlobalFilter,
    manualFiltering: true,

    // Configuración de paginación (.fullList: catálogo completo, sin controles)
    enablePagination,
    manualPagination,
    rowCount, // Pasar rowCount cuando está disponible (ej: entidades cacheables)
    paginationDisplayMode: 'pages',
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50],
    },

    // Configuración de expansión
    enableExpanding,
    getSubRows,
    renderDetailPanel: renderDetailPanel || (enableExpanding
      ? ({ row }) => {
          const rowId = getRowId ? getRowId(row.original as T) : (row.original as any).id;
          if (isLoadingSubRows && isLoadingSubRows(rowId)) {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            );
          }
          return null;
        }
      : undefined),

    // Configuración de selección
    enableRowSelection: multiSelectMode || enableRowSelection,
    enableMultiRowSelection: multiSelectMode,
    enableSelectAll: multiSelectMode,
    onRowSelectionChange: multiSelectMode ? handleRowSelectionChange : undefined,
    positionToolbarAlertBanner: 'none', // Usamos nuestro propio toolbar

    // Configuración visual
    enableColumnResizing,
    enableColumnOrdering,
    enableFullScreenToggle,
    enableHiding,
    enableColumnActions: false,
    layoutMode: 'grid',

    displayColumnDefOptions: {
      'mrt-row-expand': { size: 40 },
      'mrt-row-select': { size: 40 },
    },

    // ID de fila
    getRowId: getRowId ? (row) => getRowId(row as T) : undefined,

    // Eventos de fila
    muiTableBodyRowProps: ({ row }) => {
      const rowId = getRowId ? getRowId(row.original as T) : (row.original as any).id;
      const isSelected = selectedRowId != null && rowId === selectedRowId;
      return {
        onClick: onRowClick ? (event: React.MouseEvent) => onRowClick(row.original as T, event) : undefined,
        sx: {
          cursor: onRowClick ? 'pointer' : 'default',
          backgroundColor: isSelected ? 'action.selected' : undefined,
          '&:hover': {
            backgroundColor: isSelected ? 'action.selected' : 'action.hover',
          },
        },
        ...(typeof muiTableBodyRowProps === 'function' ? (muiTableBodyRowProps as any)({ row }) : (muiTableBodyRowProps || {})),
      };
    },

    muiTableProps: {
      sx: { tableLayout: 'fixed' },
      ...muiTableProps,
    },

    muiTableBodyCellProps: {
      sx: { fontSize: '0.8125rem', whiteSpace: 'pre-line' },
    },

    muiTableHeadCellProps: {
      sx: { fontSize: '0.8125rem', fontWeight: 600 },
    },

    // Mostrar toolbar solo en fullscreen para permitir salir
    enableTopToolbar: isFullScreen,
    // En fullscreen: mostrar solo el botón de salir (a la derecha)
    renderToolbarInternalActions: isFullScreen
      ? () => (
          <Tooltip title="Salir de pantalla completa">
            <IconButton onClick={() => handleIsFullScreenChange(false)}>
              <FullscreenExitIcon />
            </IconButton>
          </Tooltip>
        )
      : undefined,

    localization: getLocalization(language),
  });

  // ==========================================================================
  // Auto-fit por doble-click en el handle de resize (tipo Excel)
  // ==========================================================================
  // Captura el doble-click ANTES de que el handle de MRT haga resetSize (su
  // onDoubleClick no llama stopPropagation, así que la fase de captura lo gana).
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const handleAutoFitDoubleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!enableColumnResizing) return;
    const target = e.target as HTMLElement;
    const handle = target.closest(
      '.Mui-TableHeadCell-ResizeHandle-Wrapper, .Mui-TableHeadCell-ResizeHandle-Divider'
    );
    if (!handle) return;
    const th = handle.closest('th');
    const tr = th?.parentElement;
    if (!th || !tr) return;
    const columnIndex = Array.from(tr.children).indexOf(th);
    if (columnIndex < 0) return;
    const container = gridContainerRef.current;
    if (!container) return;
    // Suprime el resetSize nativo del handle.
    e.stopPropagation();
    e.preventDefault();
    autoFitColumnWidth(container, table as any, columnIndex);
  }, [enableColumnResizing, table]);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <>
      <div ref={gridContainerRef} style={{ display: 'contents' }} onDoubleClickCapture={handleAutoFitDoubleClick}>
        <MaterialReactTable table={table} />
      </div>

      {/* Menú de acciones de fila */}
      {rowActions && rowActions.length > 0 && (
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {rowActions
            .filter((action) => {
              if (!actionMenuRow) return true;
              return action.isVisible ? action.isVisible(actionMenuRow) : true;
            })
            .map((action, index) => {
              const isEnabled = actionMenuRow && action.isEnabled ? action.isEnabled(actionMenuRow) : true;
              return (
                <MenuItem key={index} onClick={(e) => handleActionClick(action, e)} disabled={!isEnabled}>
                  {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
                  <ListItemText>{action.label}</ListItemText>
                </MenuItem>
              );
            })}
        </Menu>
      )}
    </>
  );
}

// Observar cambios de MobX
export const DataGrid = observer(DataGridInner) as typeof DataGridInner;

// ============================================================================
// Auto-fit de ancho de columna (doble-click en el handle de resize, tipo Excel)
// ============================================================================
// MRT/TanStack no traen auto-fit por contenido: su doble-click en el handle solo
// resetea al ancho por defecto. Acá medimos el contenido (header + celdas
// renderizadas) con un canvas offscreen y seteamos el ancho. Solo filas cargadas
// (igual que Excel ajusta a lo visible).

let autoFitCanvas: HTMLCanvasElement | null = null;
const AUTO_FIT_PADDING = 34; // padding de celda + ícono de sort + margen de seguridad

function getAutoFitContext(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (!autoFitCanvas) autoFitCanvas = document.createElement('canvas');
  return autoFitCanvas.getContext('2d');
}

function measureCellText(ctx: CanvasRenderingContext2D, el: HTMLElement, text: string): number {
  if (!text) return 0;
  const cs = getComputedStyle(el);
  ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  return ctx.measureText(text).width;
}

/**
 * Calcula y aplica el ancho que ajusta al contenido de la columna en `columnIndex`.
 * Mapea por posición de columna (header th ↔ celdas del cuerpo). Persiste vía
 * table.setColumnSizing (que enruta al store por onColumnSizingChange).
 */
function autoFitColumnWidth(
  container: HTMLElement,
  table: { getVisibleLeafColumns: () => any[]; setColumnSizing: (u: any) => void },
  columnIndex: number,
): void {
  const ctx = getAutoFitContext();
  if (!ctx) return;

  const col = table.getVisibleLeafColumns()[columnIndex];
  if (!col || (typeof col.getCanResize === 'function' && !col.getCanResize())) return;

  const thead = container.querySelector('thead');
  const tbody = container.querySelector('tbody');
  let max = 0;

  const headerRow = thead?.querySelector('tr');
  const th = headerRow?.children[columnIndex] as HTMLElement | undefined;
  if (th) max = Math.max(max, measureCellText(ctx, th, (th.textContent || '').trim()));

  if (tbody) {
    for (const row of Array.from(tbody.children)) {
      const td = (row as HTMLElement).children[columnIndex] as HTMLElement | undefined;
      // Filas de detail-panel tienen un solo td con colSpan → su children[columnIndex] no existe.
      if (!td) continue;
      const text = (td.textContent || '').trim();
      if (text) max = Math.max(max, measureCellText(ctx, td, text));
    }
  }
  if (max === 0) return;

  const minSize = col.columnDef?.minSize ?? 40;
  const maxSize = col.columnDef?.maxSize ?? 1000;
  const width = Math.max(minSize, Math.min(maxSize, Math.ceil(max + AUTO_FIT_PADDING)));

  table.setColumnSizing((old: Record<string, number>) => ({ ...old, [col.id]: width }));
}

// ============================================================================
// Localizaciones
// ============================================================================

function getLocalization(language: 'es' | 'en' | 'pt'): Partial<MRT_TableOptions<any>['localization']> {
  const locales: Record<string, Partial<MRT_TableOptions<any>['localization']>> = {
    es: {
      actions: 'Acciones',
      cancel: 'Cancelar',
      clearFilter: CLEAR_FILTER_LABEL.es,
      clearSearch: 'Limpiar búsqueda',
      clearSort: 'Limpiar ordenamiento',
      columnActions: 'Acciones de columna',
      copiedToClipboard: 'Copiado al portapapeles',
      edit: 'Editar',
      expand: 'Expandir',
      expandAll: 'Expandir todo',
      filterByColumn: 'Filtrar por {column}',
      filterMode: 'Modo de filtro: {filterType}',
      grab: 'Agarrar',
      groupByColumn: 'Agrupar por {column}',
      groupedBy: 'Agrupado por ',
      hideAll: 'Ocultar todo',
      hideColumn: 'Ocultar columna {column}',
      noRecordsToDisplay: 'No hay registros para mostrar',
      noResultsFound: 'No se encontraron resultados',
      of: 'de',
      pin: 'Fijar',
      pinToLeft: 'Fijar a la izquierda',
      pinToRight: 'Fijar a la derecha',
      resetColumnSize: 'Restablecer tamaño de columna',
      resetOrder: 'Restablecer orden',
      rowActions: 'Acciones de fila',
      rowNumber: '#',
      rowNumbers: 'Números de fila',
      rowsPerPage: 'Filas por página',
      save: 'Guardar',
      search: 'Buscar',
      selectedCountOfRowCountRowsSelected: '{selectedCount} de {rowCount} fila(s) seleccionada(s)',
      showAll: 'Mostrar todo',
      showAllColumns: 'Mostrar todas las columnas',
      showHideColumns: 'Mostrar/Ocultar columnas',
      showHideFilters: 'Mostrar/Ocultar filtros',
      showHideSearch: 'Mostrar/Ocultar búsqueda',
      sortByColumnAsc: 'Ordenar por {column} ascendente',
      sortByColumnDesc: 'Ordenar por {column} descendente',
      sortedByColumnAsc: 'Ordenado por {column} ascendente',
      sortedByColumnDesc: 'Ordenado por {column} descendente',
      thenBy: ', luego por ',
      toggleDensity: 'Alternar densidad',
      toggleFullScreen: 'Alternar pantalla completa',
      toggleSelectAll: 'Alternar seleccionar todo',
      toggleSelectRow: 'Alternar seleccionar fila',
      toggleVisibility: 'Alternar visibilidad',
      ungroupByColumn: 'Desagrupar por {column}',
      unpin: 'Desfijar',
      unpinAll: 'Desfijar todo',
    },
    en: {
      clearFilter: CLEAR_FILTER_LABEL.en,
    },
    pt: {
      actions: 'Ações',
      cancel: 'Cancelar',
      clearFilter: CLEAR_FILTER_LABEL.pt,
      clearSearch: 'Limpar busca',
      clearSort: 'Limpar ordenação',
      noRecordsToDisplay: 'Nenhum registro para exibir',
      noResultsFound: 'Nenhum resultado encontrado',
      of: 'de',
      rowsPerPage: 'Linhas por página',
      search: 'Buscar',
      showHideColumns: 'Mostrar/Ocultar colunas',
      showHideFilters: 'Mostrar/Ocultar filtros',
    },
  };

  return locales[language] || {};
}

export default DataGrid;
